import * as io from "io-ts";
import t from "../../i18n";
import { DateTime, Interval } from "luxon";
import type { DateValue } from "react-aria";
import { parseAbsolute } from "@internationalized/date";

export interface AggregationPreset<T extends DateValue, PresetId extends string = string> {
  label: string;
  startDate: T | (() => T);
  endDate: T | (() => T);
  metadata?: { id: PresetId } & { [key: string]: unknown };
}

export const AggregationPeriod = io.keyof({
  CYCLE: null,
  CYCLE_2: null,
  P1M: null,
  P3M: null,
  P6M: null,
  P1Y: null,
  ALL: null,
});

export type AggregationPeriod = io.TypeOf<typeof AggregationPeriod>;
export type AggregationInterval = { from: Date; to: Date };
export type AggregationDateRange = { start: DateValue; end: DateValue };
export type AggregationDateRangeBounds = { min: DateValue; max: DateValue };

export const aggregationPeriodLabel = (period: AggregationPeriod): string => {
  if (period === "CYCLE") return t("period.selector.last_group");
  if (period === "CYCLE_2") return t("period.selector.last_two_groups");
  if (period === "P1M") return t("period.selector.1m.long");
  if (period === "P3M") return t("period.selector.3m.long");
  if (period === "P6M") return t("period.selector.6m.long");
  if (period === "P1Y") return t("period.selector.1y.long");
  if (period === "ALL") return t("period.selector.all");
  return "unknown period";
};

export const aggregationPeriodLabelLong = (period: AggregationPeriod): string => {
  if (period === "ALL") return t("period.selector.all.long");
  return aggregationPeriodLabel(period);
};

const clampAggregationDateValue = (
  value: DateValue,
  bounds: AggregationDateRangeBounds,
): DateValue => {
  if (value.compare(bounds.min) < 0) return bounds.min;
  if (value.compare(bounds.max) > 0) return bounds.max;
  return value;
};

export const normalizeAggregationDateRange = (
  value: AggregationDateRange,
  previousValue: AggregationDateRange,
  bounds: AggregationDateRangeBounds,
): AggregationDateRange => {
  let start = clampAggregationDateValue(value.start, bounds);
  let end = clampAggregationDateValue(value.end, bounds);

  if (start.compare(end) <= 0) return { start, end };

  const startChanged = previousValue.start.compare(start) !== 0;
  const endChanged = previousValue.end.compare(end) !== 0;

  if (startChanged && !endChanged) {
    end = start;
  } else if (endChanged && !startChanged) {
    start = end;
  } else {
    start = end;
  }

  return { start, end };
};

/** @throws */
export const aggregationIntervalFromPeriod = (
  options:
    | {
        period: Extract<AggregationPeriod, "P1M" | "P3M" | "P6M" | "P1Y">;
        timezone: string;
        from?: Date;
      }
    | {
        period: Extract<AggregationPeriod, "CYCLE" | "CYCLE_2">;
        timezone: string;
        intervals: { from: Date; to: Date }[];
        from?: Date;
      }
    | {
        period: Extract<AggregationPeriod, "ALL">;
        timezone: string;
        limit: { from: Date; to: Date };
      },
): { from: Date; to: Date; period: AggregationPeriod | null } => {
  if (options.period === "CYCLE" || options.period === "CYCLE_2") {
    if (options.intervals.length === 0) {
      throw new Error("can't define cycle interval from empty ranges");
    }

    const currentCycle = options.intervals.at(-1);

    /** @note currently our backend always return at least one group */
    const previousCycle = options.intervals.at(-2) ?? options.intervals.at(-1);

    const [from, to] =
      options.period === "CYCLE"
        ? [currentCycle?.from, new Date()]
        : [previousCycle?.from, new Date()];
    if (!from || !to) {
      throw new Error("invalid range");
    }

    from.setMilliseconds(0);
    to.setMilliseconds(999);
    return { from, to, period: options.period };
  }

  if (options.period === "ALL") {
    const from = DateTime.fromJSDate(options.limit.from)
      .setZone(options.timezone)
      .set({ millisecond: 0 })
      .toJSDate();
    const to = DateTime.now().setZone(options.timezone).set({ millisecond: 999 }).toJSDate();

    return { from, to, period: options.period };
  }

  const now = options.from ? DateTime.fromJSDate(options.from).toISO() : DateTime.now().toISO();
  const interval = Interval.fromISO(`${options.period}/${now}`, { zone: options.timezone });
  if (!interval.isValid || !interval.start || !interval.end) {
    throw new Error("invalid interval");
  }
  const from = interval.start.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate();
  const to = interval.end.set({ hour: 23, minute: 59, second: 59, millisecond: 999 }).toJSDate();
  return { from, to, period: options.period };
};

export const aggregationIntervalToDateValue = (
  interval: { from: Date; to: Date },
  timezone: string,
): { start: DateValue; end: DateValue } => ({
  start: parseAbsolute(interval.from.toISOString(), timezone),
  end: parseAbsolute(interval.to.toISOString(), timezone),
});

export const aggregationIntervalFromDateValue = (
  dateValues: { start: DateValue; end: DateValue },
  timezone: string,
): { from: Date; to: Date } => ({
  from: dateValues.start.toDate(timezone),
  to: dateValues.end.toDate(timezone),
});

export const aggregationIntervalWithDurationFromDateValue = (
  dateValues: { start: DateValue; end: DateValue },
  timezone: string,
  presets: AggregationPreset<DateValue, AggregationPeriod>[],
): { from: Date; to: Date; period: AggregationPeriod | null } => {
  const interval = aggregationIntervalFromDateValue(dateValues, timezone);
  const period = aggregationPeriodFromPresets({ dateValues, presets });
  return { ...interval, period };
};

export const calendarPresetFromAggregationInterval = <P extends AggregationPeriod>({
  interval,
  timezone,
  period,
}: {
  period: P;
  interval: ReturnType<typeof aggregationIntervalFromPeriod>;
  timezone: string;
}): AggregationPreset<DateValue, P> => {
  const dateValueInterval = aggregationIntervalToDateValue(interval, timezone);
  return {
    startDate: dateValueInterval.start,
    endDate: dateValueInterval.end,
    label: aggregationPeriodLabel(period),
    metadata: { id: period },
  };
};

export const aggregationPeriodFromPresets = ({
  dateValues,
  presets,
}: {
  dateValues: { start: DateValue; end: DateValue };
  presets: AggregationPreset<DateValue, AggregationPeriod>[];
}): AggregationPeriod | null =>
  presets.find(p => {
    const start = typeof p.startDate === "function" ? p.startDate() : p.startDate;
    const end = typeof p.endDate === "function" ? p.endDate() : p.endDate;

    const match =
      start.set({ millisecond: 0 }).compare(dateValues.start.set({ millisecond: 0 })) === 0 &&
      /**
       * @note for the CYCLE, CYCLE_2, ALL end date, we want to be a bit loose
       * to match the day as we will not refresh the `Date.now()`,
       * either in the preset or the interval for the comparison
       */
      end
        .set({ millisecond: 999, hour: 23, minute: 59, second: 59 })
        .compare(dateValues.end.set({ millisecond: 999, hour: 23, minute: 59, second: 59 })) === 0;
    return match;
  })?.metadata?.id ?? null;

export const computePreviousAggregationInterval = (interval: {
  from: Date;
  to: Date;
}): { from: Date; to: Date } => ({
  from: new Date(interval.from.getTime() - (interval.to.getTime() - interval.from.getTime())),
  to: interval.from,
});

export type EntityWithInterval = { startDate: number; endDate: number };

export const mapIntervalFromEntities = <T extends EntityWithInterval>(
  xs: T[],
): { from: Date; to: Date }[] =>
  xs.map(x => ({ from: new Date(x.startDate), to: new Date(x.endDate) }));
